import { useState } from "react";
import { Axes, ChartSvg, LinearScale, M, NiceDomain, NiceTicks, SERIES, SURFACE_STROKE, Tooltip, W, H, type TooltipState } from "./ChartKit";
import type { Point } from "./playgroundData";

interface ScatterProps {
  points: Point[];
  curve?: (x: number) => number;
  xLabel: string;
  yLabel: string;
  yDomain?: [number, number];
}

function CurvePath(curve: (x: number) => number, x0: number, x1: number, x: (v: number) => number, y: (v: number) => number): string {
  return Array.from({ length: 61 }, (_, i) => x0 + ((x1 - x0) * i) / 60).map((v, i) => `${i ? "L" : "M"}${x(v)} ${y(curve(v))}`).join(" ");
}

export function ScatterChart({ points, curve, xLabel, yLabel, yDomain }: ScatterProps) {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const [x0, x1] = NiceDomain(Math.min(...points.map((p) => p.x)), Math.max(...points.map((p) => p.x)));
  const [y0, y1] = yDomain ?? NiceDomain(Math.min(...points.map((p) => p.y)), Math.max(...points.map((p) => p.y)));
  const x = LinearScale([x0, x1], [M.left, W - M.right]);
  const y = LinearScale([y0, y1], [H - M.bottom, M.top]);
  const yTicks = yDomain ? NiceTicks(Math.max(0, y0), Math.min(1, y1)) : NiceTicks(y0, y1);
  return (
    <div className="relative">
      <ChartSvg label={curve ? "Scatter plot with fitted curve" : "Scatter plot of two variables"} onLeave={() => setTip(null)}>
        <Axes xTicks={NiceTicks(x0, x1)} yTicks={yTicks} x={x} y={y} xLabel={xLabel} yLabel={yLabel} />
        {curve && <path d={CurvePath(curve, x0, x1, x, y)} fill="none" strokeWidth={2} strokeLinecap="round" className={SERIES[1].stroke} />}
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => setTip({ x: x(p.x), y: y(p.y), lines: [`Point ${i + 1}`, `x = ${p.x.toFixed(1)}`, `y = ${p.y.toFixed(yDomain ? 0 : 1)}`] })}>
            <circle cx={x(p.x)} cy={y(p.y)} r={10} fill="transparent" />
            <circle cx={x(p.x)} cy={y(p.y)} r={4} strokeWidth={2} className={`${SERIES[0].fill} ${SURFACE_STROKE}`} />
          </g>
        ))}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}
